// The credit ledger (screaming.md → billing/wallet), shared by the API and the SPA and held to
// docs/openapi.json by wallet.test.ts. One body on both doors, snake_case like the rest of the
// v3 contract. Money is the Money string here and a Dinero inside the API (money.ts).
import { z } from "zod";
import { Money } from "./money";
import { PageMeta } from "./contact";

/**
 * Every reason money moves. Closed: a caller that needs a new one adds it here (D3). The
 * database checks only the shape, so growing the list needs no migration.
 */
export const WALLET_REASONS = [
  "domain.register",
  "domain.renew",
  "domain.transfer_in",
  "domain.refund",
  "topup.credit",
] as const;
export type WalletReason = (typeof WALLET_REASONS)[number];

export const isWalletReason = (value: string): value is WalletReason =>
  (WALLET_REASONS as readonly string[]).includes(value);

export const WALLET_ENTRY_KINDS = ["debit", "credit"] as const;
export type WalletEntryKind = (typeof WALLET_ENTRY_KINDS)[number];

/** The order a movement belongs to: its ref when the reason is a domain one, else none. */
export const orderRefOf = (reason: string, ref: string): string | null => (reason.startsWith("domain.") ? ref : null);

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

export const Balance = z.object({ balance: Money, currency: z.literal("IDR") });
export type Balance = z.infer<typeof Balance>;

export const WalletEntry = z.object({
  id: Base62Id,
  kind: z.enum(WALLET_ENTRY_KINDS),
  amount: Money,
  balance_after: Money,
  reason: z.string(),
  order_id: Base62Id.nullable(),
  created_at: z.iso.datetime(),
});
export type WalletEntry = z.infer<typeof WalletEntry>;

/**
 * from and to are calendar days in Asia/Jakarta, both inclusive (D10). ISO dates compare as
 * strings, so from <= to needs no Date.
 */
export const WalletEntryListQuery = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    per_page: z.coerce.number().int().min(1).max(100).default(25),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    kind: z.enum(WALLET_ENTRY_KINDS).optional(),
  })
  .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: "from_after_to",
    path: ["from"],
  });
export type WalletEntryListQuery = z.infer<typeof WalletEntryListQuery>;

export const BalanceResponse = z.object({ data: Balance });
export const WalletEntryListResponse = z.object({ data: z.array(WalletEntry), meta: PageMeta });
